import React from "react";
import { Link } from "react-router-dom";
import { Heart, Shield, HandHeart, ArrowRight, Sparkles, Users, Clock, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { companyInfo, faqs } from "../data/mockData";
import SectionHeader from "../components/SectionHeader";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "../components/ui/accordion";

const About = () => {
  return (
    <div data-testid="about-page" className="bg-stone-50">
      {/* HERO */}
      <section className="relative bg-amber-50/30 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 animate-fade-up">
              <p className="text-amber-700 text-sm uppercase tracking-[0.3em] font-semibold mb-5">
                About Us
              </p>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-slate-900 leading-[1.05] tracking-tight">
                Rooted in love.{" "}
                <span className="italic text-amber-700">Built on trust.</span>
              </h1>
              <p className="mt-8 text-lg lg:text-xl text-slate-600 leading-relaxed max-w-2xl">
                {companyInfo.name} was founded with a simple belief: every senior deserves to live their later years with dignity, comfort, and genuine companionship — in the home they love.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="aspect-square rounded-[2.5rem] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1750853730688-61ba53660e0d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxjYXJlZ2l2ZXIlMjBzZW5pb3IlMjBwYXRpZW50JTIwc21pbGluZ3xlbnwwfHx8fDE3NzgwMTE0NTh8MA&ixlib=rb-4.1.0&q=85"
                  alt="Happy senior smiling"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION / VALUES */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <p className="text-amber-700 text-sm uppercase tracking-[0.3em] font-semibold mb-4">
                Our Mission
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl text-slate-900 leading-tight tracking-tight mb-8">
                To care for others like we care for our own.
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                We started this company because we saw families struggling — torn between their own responsibilities and their deep desire to care for aging parents. We watched seniors lose their joy, their confidence, their independence.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                So we built something different: a care company where every caregiver is chosen for their heart, every schedule is built around the client, and every family is treated like our own.
              </p>
              <p className="font-script text-3xl text-amber-700 mt-8">
                — That's the promise we live by every day.
              </p>
            </div>

            <div className="bg-slate-900 text-white rounded-[2.5rem] p-10 lg:p-12">
              <p className="text-amber-400 text-sm uppercase tracking-[0.3em] font-semibold mb-4">
                Our Core Values
              </p>
              <h3 className="font-serif text-3xl lg:text-4xl text-white leading-tight mb-10">
                Compassion. Respect. Trust.
              </h3>

              <div className="space-y-8">
                <div className="flex gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-amber-600 flex items-center justify-center flex-shrink-0">
                    <Heart className="w-6 h-6 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-serif text-xl text-white mb-2">Compassion</h4>
                    <p className="text-stone-300 leading-relaxed">
                      We listen with open hearts and respond with warmth. No two clients are alike — and neither is our care.
                    </p>
                  </div>
                </div>

                <div className="flex gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-amber-600 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-serif text-xl text-white mb-2">Respect</h4>
                    <p className="text-stone-300 leading-relaxed">
                      Every client is an individual with a lifetime of wisdom, stories, and preferences. We honor who they are.
                    </p>
                  </div>
                </div>

                <div className="flex gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-amber-600 flex items-center justify-center flex-shrink-0">
                    <HandHeart className="w-6 h-6 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-serif text-xl text-white mb-2">Trust</h4>
                    <p className="text-stone-300 leading-relaxed">
                      Consistency, reliability, and open communication build the kind of trust that lasts a lifetime.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-20 lg:py-28 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Why Choose Blessing"
            title="The difference is in every detail."
            description="Great care isn't one thing — it's a thousand small ones. Here's what you can expect from us."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
            {[
              {
                icon: Users,
                title: "Carefully Matched",
                text: "We hand-pair caregivers to clients based on personality, needs, and preferences."
              },
              {
                icon: Shield,
                title: "Vetted & Insured",
                text: "Every caregiver is background-checked, trained, and fully insured for your peace of mind."
              },
              {
                icon: Clock,
                title: "Flexible Hours",
                text: "From a few hours a week to 24/7 live-in care — care that fits your life."
              },
              {
                icon: Sparkles,
                title: "Ongoing Check-Ins",
                text: "We stay in close contact with families, adjusting care as needs evolve."
              }
            ].map((item, i) => (
              <div
                key={i}
                data-testid={`why-choose-${i}`}
                className="bg-white rounded-3xl p-8 border border-stone-200/70 hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mb-6">
                  <item.icon className="w-6 h-6 text-amber-700" strokeWidth={1.5} />
                </div>
                <h4 className="font-serif text-xl text-slate-900 mb-3">{item.title}</h4>
                <p className="text-slate-600 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Frequently Asked"
            title="Answers to help you feel at ease."
            description="New to companion care? Here are some common questions we hear from families."
          />

          <div className="mt-14">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="bg-white border border-stone-200/70 rounded-2xl px-6 hover:border-amber-700/40 transition-colors"
                >
                  <AccordionTrigger className="text-left font-serif text-lg text-slate-900 hover:no-underline py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-amber-700 text-white py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6">
            Ready to meet the team that will treat your loved one like family?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <a href={`tel:${companyInfo.phone.replace(/\D/g, "")}`}>
              <Button className="bg-white text-amber-800 hover:bg-stone-100 rounded-full px-8 py-6 w-full sm:w-auto">
                <Phone className="w-4 h-4 mr-2" />
                {companyInfo.phone}
              </Button>
            </a>
            <Link to="/contact">
              <Button
                variant="outline"
                className="border-2 border-white text-white bg-transparent hover:bg-white hover:text-amber-800 rounded-full px-8 py-6 w-full sm:w-auto"
              >
                Start the conversation
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
